// ActionPage — the one action surface. Whatever you open an action from,
// this is what you read and edit: the Book's recto, the workbench pane, zen.
// There is deliberately no variant flag. If the two hosts ever need to look
// different, that difference belongs in the host's frame around this page
// (zen chrome, the pane's close ✕, the re-entry card), never inside it.
//
// The host owns the box — padding, width, background. This component owns
// the content, in one fixed order: breadcrumb · title · status · meta ·
// how it ended · next step · notes · checklist.
import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Download } from 'lucide-react';
import ActionEditor from './ActionEditor';
import { stateWord } from '../stateWords';
import { composeActionMarkdown, slugify } from '../actionMarkdown';
import { isPast } from '../dates';

function CopyMarkdownButton({ getMarkdown, getHTML }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    const md = getMarkdown();
    try {
      const html = getHTML ? getHTML() : null;
      if (html && navigator.clipboard.write && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({
          'text/plain': new Blob([md], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        })]);
      } else {
        await navigator.clipboard.writeText(md);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      try { await navigator.clipboard.writeText(md); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch {}
    }
  };
  return (
    <button
      onClick={doCopy}
      className="p-1 rounded themed-text-muted hover:themed-text transition-colors"
      title="Copy as Markdown — paste into AI tools, Bear, VS Code (rich text into Docs/Gmail)"
    >
      {copied ? <Check size={13} style={{ color: '#16a34a' }} /> : <Copy size={13} />}
    </button>
  );
}

export default function ActionPage({
  action,
  projects,
  goals,
  leading,                 // optional node at the head of the breadcrumb (the Book's ✕ close)
  updateAction,
  setActionStatus,
  toggleActionPho,
  toggleActionComplete,
  addChecklistItem,
  onDelete,
  onOpenGoal,
  onOpenProject,
  timeAgo,
  // Optional wiring for a host that drives the editor from outside (zen):
  // a ref to the ActionEditor handle, and its focus / navigate callbacks.
  editorRef,
  onEditorFocus,
  hideCrumb,
  onEditorNavigate,
}) {
  const titleRef = useRef(null);
  const ownEditorRef = useRef(null);
  const edRef = editorRef || ownEditorRef;
  const dateInputRefs = useRef({});   // "add a day" opens these via showPicker()

  // Law 5: no hover-opened panels. The ··· menu opens on a deliberate click and
  // closes on the next click outside it, or Esc. Esc is captured and stopped so
  // it closes the menu without also walking the host out of zen.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setMenuOpen(false); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true); };
  }, [menuOpen]);

  if (!action) return null;

  const project = action.linkType === 'project' && action.linkId != null
    ? projects.find(p => p.id === action.linkId) || null
    : null;
  const goal = project && project.goalId != null
    ? (goals || []).find(g => g.id === project.goalId) || null
    : null;

  // The three states wear the law's words: to do / doing / done (the cure, Nov 4 2026)
  const statusWord = stateWord(action);
  const setChecklist = (list) => updateAction(action.id, { checklist: list });

  const exportMd = () => {
    const md = composeActionMarkdown(action, edRef.current?.getMarkdown() || action.notes || '');
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugify(action.text)}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const StatusPill = ({ label, active, onClick }) => (
    <button onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${active
        ? 'themed-primary-bg text-white border-transparent'
        : 'themed-border themed-text-muted hover:themed-text'}`}>{label}</button>
  );

  return (
    <div className="text-left">

      {/* The crumb left the page Oct 9 2026 — Paul: "get rid of
          breadcrumbing." The footer below names the project instead. */}
      {false && (leading || goal || project) && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {leading}
          {leading && (goal || project) && <span className="text-[11px] themed-text-muted">·</span>}
          {goal && (
            <button onClick={() => onOpenGoal && onOpenGoal(goal.id)}
              className="text-[11px] themed-text-muted hover:themed-text truncate"
              title={`Open ${goal.name}`}>‹ {goal.name}</button>
          )}
          {project && (<>
            {goal && <span className="text-[11px] themed-text-muted">/</span>}
            <button onClick={() => onOpenProject && onOpenProject(project.id)}
              className="text-[11px] themed-text-muted hover:themed-text truncate"
              title={`Open ${project.name}`}>{project.name}</button>
          </>)}
        </div>
      )}

      {/* 2 — the title, editable in place */}
      <div className="flex items-start gap-2 mt-1">
        <button onClick={() => toggleActionPho(action.id)}
          className="text-lg leading-none mt-1.5 flex-shrink-0 transition-opacity"
          style={{ color: action.pho ? 'var(--color-pho)' : 'var(--color-textMuted)', opacity: action.pho ? 1 : 0.35 }}
          title={action.pho ? 'Unmark focus' : 'Mark as focus'}>✦</button>
        <h1
          key={`title-${action.id}`}
          ref={titleRef}
          className="text-[17px] font-bold themed-text leading-snug flex-1 min-w-0 focus:outline-none"
          contentEditable
          suppressContentEditableWarning
          title="Click to rename"
          onBlur={(e) => {
            const t = e.target.innerText.trim();
            if (t && t !== action.text) updateAction(action.id, { text: t });
            else if (!t) e.target.innerText = action.text; // never save an empty title
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation(); // cancel the rename, don't close the page
              e.target.innerText = action.text;
              e.target.blur();
            }
          }}
        >
          {action.text}
        </h1>
        <div ref={menuRef} className="relative flex-shrink-0 mt-1">
          <button
            onClick={() => setMenuOpen(o => !o)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            className="p-1.5 rounded themed-text-muted hover:themed-text transition-colors"
            title="More"
          >···</button>
          {menuOpen && (
            <div role="menu" className="absolute right-0 top-7 themed-background border themed-border rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  const h1 = titleRef.current;
                  if (!h1) return;
                  h1.focus();
                  const range = document.createRange();
                  range.selectNodeContents(h1);
                  const sel = window.getSelection();
                  sel.removeAllRanges();
                  sel.addRange(range);
                }}
                className="w-full text-left px-3 py-1.5 text-sm themed-text hover:themed-surface-hover"
              >
                Rename
              </button>
              <button
                onClick={() => { setMenuOpen(false); onDelete && onDelete(action.id); }}
                className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:themed-surface-hover"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 3 — ONE pill, the current status. A click walks it forward:
          open → active → done (the honest ritual) → open again. */}
      <div className="flex items-center mt-3" title="Click to change: open → active → done">
        <StatusPill label={statusWord} active
          onClick={() => {
            if (action.completed) { toggleActionComplete(action.id); return; }
            if (action.status === 'doing' || action.status === 'done') { toggleActionComplete(action.id); return; }
            setActionStatus(action.id, 'doing');
          }} />
      </div>


      {/* 5 — how it ended (completed only) */}
      {action.completed && (
        <div className="mt-5 rounded-lg px-4 py-4" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderLeft: '3px solid #16a34a' }}>
          <div className="flex items-center gap-2 mb-2.5">
            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#16a34a' }}>How it ended</p>
            {action.completedDate && (
              <span className="text-[10px] themed-text-muted">· {timeAgo(action.completedDate)}</span>
            )}
            {action.resolutionType && (
              <span className={`ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                action.resolutionType === 'done'   ? 'bg-green-100 text-green-700' :
                action.resolutionType === 'wontdo' ? 'bg-gray-100 text-gray-600'   :
                                                     'bg-orange-100 text-orange-700'
              }`}>
                {action.resolutionType === 'done' ? '✓ Done' : action.resolutionType === 'wontdo' ? '✗ Won\'t do' : '→ Moved'}
              </span>
            )}
          </div>
          <textarea
            key={action.id + '-landing'}
            defaultValue={action.resolutionComment || ''}
            placeholder="How did this conclude? What did you decide or ship?"
            rows={3}
            className="w-full text-[13px] themed-text bg-transparent resize-none focus:outline-none placeholder:themed-text-muted placeholder:opacity-40 leading-relaxed"
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== (action.resolutionComment || '').trim()) {
                updateAction(action.id, { resolutionComment: v || null });
              }
            }}
          />
        </div>
      )}

      {/* The "Next step" field left the page Oct 7 2026 — Paul: "it feels
          like work I don't need. Can it just be a note for the task and its
          status?" The data field survives (the MCP may still write it);
          the page is a note until you want to add sections. */}

      {/* 7 — notes */}
      <div className="flex items-center mt-6 mb-2" style={{ maxWidth: '68ch' }}>
        <p className="text-[10px] uppercase tracking-widest themed-text-muted font-semibold">Notes</p>
        <div className="flex items-center gap-0.5 ml-auto">
          <CopyMarkdownButton
            getMarkdown={() => composeActionMarkdown(action, edRef.current?.getMarkdown() || action.notes || '')}
            getHTML={() => `<h1>${action.text || ''}</h1>` + (edRef.current?.getHTML() || '')}
          />
          <button
            onClick={exportMd}
            className="p-1 rounded themed-text-muted hover:themed-text transition-colors"
            title="Download as .md file"
          >
            <Download size={13} />
          </button>
        </div>
      </div>
      <div style={{ maxWidth: '68ch' }}>
        <ActionEditor
          key={`act-${action.id}`}
          ref={edRef}
          content={action.notes || ''}
          onSave={(md) => updateAction(action.id, { notes: md })}
          onNavigate={onEditorNavigate}
          onFocus={onEditorFocus}
        />
      </div>

      {/* 8 — checklist */}
      <p className="text-[10px] uppercase tracking-widest themed-text-muted font-semibold mt-6 mb-2">Checklist</p>
      {(action.checklist || []).map(c => (
        <div key={c.id} className="flex items-start gap-2 py-1 group/ci">
          <button onClick={() => setChecklist((action.checklist || []).map(x => x.id === c.id ? { ...x, completed: !x.completed } : x))}
            className="text-[13px] leading-relaxed flex-shrink-0 themed-text-muted hover:themed-text"
            title={c.completed ? 'Mark undone' : 'Mark done'}>{c.completed ? '✓' : '○'}</button>
          <span className={`text-[13px] leading-relaxed flex-1 ${c.completed ? 'themed-text-muted' : 'themed-text'}`}>{c.text}</span>
          <button onClick={() => setChecklist((action.checklist || []).filter(x => x.id !== c.id))}
            className="text-[11px] themed-text-muted opacity-0 group-hover/ci:opacity-100 transition-opacity flex-shrink-0"
            title="Remove">✕</button>
        </div>
      ))}
      <input
        key={`ci-${action.id}-${(action.checklist || []).length}`}
        placeholder="Add a step…"
        onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value.trim()) { addChecklistItem(action.id, e.target.value); e.target.value = ''; } }}
        className="w-full text-[13px] themed-text bg-transparent border-0 focus:outline-none py-1 pl-5"
        style={{ maxWidth: '62ch' }} />

      {/* 9 — the footer: where this task lives and when. Demoted from the
          masthead on Paul's word (Oct 9 2026): "put it way out of sight —
          bottom of the note, some footer maybe." */}
      <div className="mt-10 pt-3 border-t themed-border-light opacity-75 hover:opacity-100 transition-opacity">
      <div className="flex items-center gap-5 flex-wrap mt-3">
        {/* Grouped by goal so a mis-pick can't silently move an action into
            another goal's project — fifteen library goals share a project
            name, and the flat list made them indistinguishable (UX-13). */}
        <select
          value={project ? String(project.id) : ''}
          onChange={e => {
            const pid = e.target.value;
            updateAction(action.id, {
              linkType: pid ? 'project' : 'unlinked',
              linkId:   pid ? parseInt(pid) : null,
            });
          }}
          className="text-[11px] themed-text-muted bg-transparent border-0 focus:outline-none cursor-pointer hover:themed-text transition-colors"
          title="Move this action to another project. Picking under another goal moves it there with it."
        >
          <option value="">No project</option>
          {(goals || []).filter(g => g.active).map(g => {
            const ps = projects.filter(p => p.active && p.goalId === g.id);
            return ps.length ? (
              <optgroup key={g.id} label={g.name}>
                {ps.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            ) : null;
          })}
          {projects.filter(p => p.active && (p.goalId == null || !(goals || []).some(g => g.active && g.id === p.goalId))).length > 0 && (
            <optgroup label="No goal">
              {projects.filter(p => p.active && (p.goalId == null || !(goals || []).some(g => g.active && g.id === p.goalId))).map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </optgroup>
          )}
        </select>
        {/* An empty control speaks its action in plain words: "add a day",
            not a 20px sliver of the browser's date UI (UX-12). */}
        {!action.completed && [
          { key: 'doDate', label: 'Do', color: '#13a244', title: 'When you plan to work on this — yours to move' },
          { key: 'dueDate', label: 'Due', color: '#dc2626', title: 'When the world needs it — the deadline' },
        ].map(f => (
          <label key={f.key} className="flex items-center gap-1.5 text-[11px] themed-text-muted cursor-pointer group/date" title={f.title}>
            <span
              className="font-semibold uppercase tracking-wider text-[9px]"
              style={action[f.key] ? { color: f.color } : {}}
            >{f.label}</span>
            {!action[f.key] && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  const el = dateInputRefs.current[f.key];
                  if (el) { if (el.showPicker) { try { el.showPicker(); } catch { el.focus(); } } else el.focus(); }
                }}
                className="text-[11px] themed-text-muted hover:themed-text underline decoration-dotted underline-offset-2 py-1"
              >add a day</button>
            )}
            <input
              type="date"
              ref={el => { dateInputRefs.current[f.key] = el; }}
              value={action[f.key] || ''}
              onChange={e => updateAction(action.id, { [f.key]: e.target.value || null })}
              className="bg-transparent border-0 focus:outline-none cursor-pointer text-[11px] themed-text-muted hover:themed-text"
              style={{
                colorScheme: 'inherit',
                ...(action[f.key]
                  ? { width: 'auto' }
                  : { width: 0, padding: 0, opacity: 0, position: 'absolute', pointerEvents: 'none' }),
                ...(action[f.key] && isPast(action[f.key]) && f.key === 'dueDate' ? { color: '#dc2626', fontWeight: 600 } : {}),
              }}
              tabIndex={action[f.key] ? 0 : -1}
            />
            {action[f.key] && (
              <button
                onClick={(e) => { e.preventDefault(); updateAction(action.id, { [f.key]: null }); }}
                className="opacity-0 group-hover/date:opacity-60 hover:!opacity-100 text-[10px]"
                title={`Clear ${f.label.toLowerCase()} date`}
              >✕</button>
            )}
          </label>
        ))}
      </div>
      {action.lastTouchedAt && !action.completed && (
        <p className="text-[10px] themed-text-muted mt-1.5 opacity-70">Last worked {timeAgo(action.lastTouchedAt)}</p>
      )}
      </div>

    </div>
  );
}
